import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import FomoFiLogo from "@/components/brand/FomoFiLogo";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Link } from "react-router-dom";
import { REDESIGN_NAV_ITEMS, navItemIsActive } from "@/config/redesignNavItems";

interface MobileSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Nav-only drawer: the profile (with theme toggle and logout) lives solely in
// the upper-right ContentHeader cluster, which renders at phone width too —
// Anthony's #5 is a move, not a copy.
export default function MobileSidebarRedesign({ open, onOpenChange }: MobileSidebarProps) {
  const { pathname } = useLocation();

  // Close on route change
  useEffect(() => {
    onOpenChange(false);
  }, [pathname, onOpenChange]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="w-[calc(280px+env(safe-area-inset-left))] p-0 pl-[env(safe-area-inset-left)] bg-sidebar text-sidebar-foreground border-sidebar-border"
        showCloseButton={false}
        // The drawer is a list of links; it has a name and nothing to describe.
        // Radix asks for a description too unless it is explicitly told there is
        // none, and an invented sentence read out before every nav item is worse
        // than no sentence.
        aria-describedby={undefined}
      >
        <SheetHeader className="h-16 flex-row items-center justify-start border-b border-sidebar-border px-5 py-0 space-y-0 gap-0">
          {/* The name of the dialog has to live on a real element, because Radix
              points the drawer's `aria-labelledby` at the id it puts on the
              title and then looks that id up in the document. `asChild` handed
              the id to `FomoFiLogo`, which takes three named props and spreads
              nothing, so the id was dropped, no element carried it, and every
              open of this drawer logged Radix's "`DialogContent` requires a
              `DialogTitle`" error -- 23 of them across 18 routes in the
              exercise sweep (BEN-141 QF11). The logo is decoration here; the
              name is the sentence below it. */}
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <FomoFiLogo size="md" />
        </SheetHeader>

        {/* Nav items */}
        <nav aria-label="Mobile navigation" className="flex-1 overflow-y-auto px-3 py-4">
          <ul role="list" className="flex flex-col gap-0.5">
            {REDESIGN_NAV_ITEMS.map((item) => {
              const isActive = navItemIsActive(item, pathname);
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    aria-current={isActive ? "page" : undefined}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150",
                      isActive
                        ? "bg-sidebar-primary text-sidebar-primary-foreground"
                        : "text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground",
                    )}
                  >
                    <item.icon
                      className="h-[18px] w-[18px] shrink-0"
                      strokeWidth={isActive ? 2 : 1.75}
                    />
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </SheetContent>
    </Sheet>
  );
}
